import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Target, Award, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function About() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        setTeam(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching team:', error);
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Innovation',
      description: 'Leading the industry with cutting-edge smart building technology and sustainable solutions.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality products and services with guaranteed results.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'Building long-term relationships based on trust, transparency, and mutual success.'
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 data-testid="about-title" className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6">
            ABOUT <span className="text-primary">GREENSMART</span>
          </h1>
          <p data-testid="about-subtitle" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Building a sustainable future with smart technology and zero capital investment
          </p>
        </motion.div>

        {/* GS Building */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="gs-building"
          className="mb-16 max-w-2xl mx-auto"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_greensmart-team/artifacts/4ugq3i0d_image.png"
            alt="GreenSmart Sustainable Building with Solar Panels"
            className="w-full h-auto rounded-lg"
          />
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          data-testid="mission-statement"
          className="glass p-12 mb-16 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 uppercase">
            OUR <span className="text-primary">MISSION</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            At GreenSmart Ltd., we're revolutionizing building management with state-of-the-art HVAC solutions and smart technology. 
            Our comprehensive package of solutions comes with zero capital investment, guaranteed energy savings, and full warranty coverage. 
            We integrate sustainable technologies to reduce environmental impact while maximizing efficiency and comfort.
          </p>
        </motion.div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12 uppercase text-center">
            OUR <span className="text-primary">VALUES</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`value-${value.title.toLowerCase()}`}
                  className="glass p-8 hover:bg-white/5 transition-all hover:glow-green"
                >
                  <Icon className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3 uppercase">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-testid="team-section"
          className="mt-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12 uppercase text-center">
            MEET OUR <span className="text-primary">TEAM</span>
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : team.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {team.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  data-testid={`team-member-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="glass p-6 hover:bg-white/5 transition-all hover:glow-green"
                >
                  {member.image_url ? (
                    <img 
                      src={member.image_url} 
                      alt={member.name} 
                      className="w-full aspect-[3/4] object-cover object-top mb-4 rounded"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="w-full aspect-square bg-primary/10 hidden items-center justify-center mb-4">
                    <span className="text-6xl font-bold text-primary">{member.name.charAt(0)}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">{member.name}</h3>
                  <p className="text-sm text-primary font-mono mb-3">{member.position}</p>
                  <p className="text-xs text-muted-foreground line-clamp-4">{member.bio}</p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Team information coming soon.</p>
            </div>
          )}
        </motion.div>

        {/* Locations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-testid="locations-section"
          className="mt-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12 uppercase text-center">
            OUR <span className="text-primary">LOCATIONS</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="/contact" className="glass p-8 hover:bg-white/5 transition-all hover:glow-green cursor-pointer">
              <MapPin className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">VANCOUVER OFFICE</h3>
              <p className="text-muted-foreground">Vancouver, BC, Canada</p>
              <span className="inline-block mt-3 text-primary text-sm font-mono uppercase">Let's Connect →</span>
            </a>
            <a href="/contact" className="glass p-8 hover:bg-white/5 transition-all hover:glow-green cursor-pointer">
              <MapPin className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">TORONTO OFFICE</h3>
              <p className="text-muted-foreground">Toronto, ON, Canada</p>
              <span className="inline-block mt-3 text-primary text-sm font-mono uppercase">Let's Connect →</span>
            </a>
            <a href="/contact" className="glass p-8 hover:bg-white/5 transition-all hover:glow-green cursor-pointer">
              <MapPin className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">MIAMI OFFICE</h3>
              <p className="text-muted-foreground">Miami, FLA, US</p>
              <span className="inline-block mt-2 px-3 py-1 bg-primary/20 text-primary text-xs font-mono uppercase">Coming Soon!</span>
              <span className="inline-block mt-3 text-primary text-sm font-mono uppercase">Let's Connect →</span>
            </a>
          </div>
        </motion.div>

        {/* YouTube Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12 uppercase text-center">
            WATCH OUR <span className="text-primary">VIDEOS</span>
          </h2>
          <div className="text-center">
            <a
              href="https://www.youtube.com/@TonyR-c7l"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-primary text-black font-mono uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-all hover:glow-green"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              VIEW OUR YOUTUBE CHANNEL
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
